import EndQuestion from '@renderer/components/Quiz/EndQuestion'
import AnswersOptions from '@renderer/components/Quiz/SingleAnswer'
import { Button } from '@renderer/components/ui/button'
import { useQuiz } from '@renderer/lib/useQuiz'
import { preventOrphanPunctuation } from '@renderer/lib/utils'
import { AnimatePresence, motion } from 'motion/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const QuizPage = () => {
  const { currentQuestion, currentIndex, score, isAnswered, totalQuestions, validateAnswers } =
    useQuiz()

  const { t } = useTranslation('translation', { keyPrefix: 'quiz' })

  const AnswerComponent = useMemo(() => {
    switch (currentQuestion?.type) {
      case 'single':
        return <AnswersOptions />
      case 'multiple':
        return <AnswersOptions />
      case 'touch':
        return null
      default:
        return null
    }
  }, [currentQuestion?.type])

  return (
    <div className="flex flex-col w-full h-full min-h-0 overflow-hidden px-6 py-3 gap-2 [@media(max-height:1100px)]:px-6 [@media(max-height:1100px)]:py-2 [@media(min-height:1101px)]:px-12 [@media(min-height:1101px)]:py-5">
      <div className="flex flex-col flex-1 min-h-0 items-center relative px-2 [@media(max-height:1100px)]:px-4 [@media(min-height:1101px)]:px-8">
        {currentQuestion?.image && (
          <div className="flex-1 min-h-0 w-full max-w-4xl flex items-center justify-center overflow-hidden py-1">
            <img
              src={`${import.meta.env.BASE_URL}assets/images/${currentQuestion?.image}`}
              alt="Question"
              className="block max-h-full max-w-full w-auto h-auto object-contain border-4 border-primary box-border"
            />
          </div>
        )}

        <div className="flex flex-col items-center w-full max-w-4xl shrink-0 [@media(max-height:1100px)]:pt-1 [@media(min-height:1101px)]:pt-2">
          <h1 className="font-display font-bold text-primary text-center leading-snug max-w-4xl text-pretty text-3xl mt-5 mb-8 [@media(max-height:1100px)]:text-2xl [@media(max-height:1100px)]:mt-4 [@media(max-height:1100px)]:mb-6 [@media(max-height:850px)]:text-xl [@media(max-height:850px)]:mt-3 [@media(max-height:850px)]:mb-5 [@media(min-height:1101px)]:text-4xl [@media(min-height:1101px)]:mt-7 [@media(min-height:1101px)]:mb-10">
            {preventOrphanPunctuation(t(`${currentQuestion.id}.title` as any) as string)}
          </h1>

          <div className="w-full">{AnswerComponent}</div>
        </div>

        <AnimatePresence mode="wait">
          {currentQuestion?.type === 'multiple' && isAnswered && (
            <motion.div
              initial={{ y: 1080 }}
              animate={{ y: 0 }}
              exit={{ y: 1080 }}
              transition={{ visualDuration: 0.3, type: 'spring', bounce: 0.2 }}
              className="absolute bottom-0 left-0 w-full flex justify-center items-center"
              id="validateButton"
            >
              <Button
                onClick={validateAnswers}
                className="bg-ratp-anis text-primary font-bold hover:bg-ratp-anis/80 active:bg-ratp-anis/60 text-xl p-6 [@media(max-height:1100px)]:text-base [@media(max-height:1100px)]:p-3"
              >
                VALIDER
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
        <EndQuestion />
      </div>
      <div id="topBar" className="flex justify-center items-center w-full shrink-0 py-1">
        <h3 className="text-primary text-base [@media(max-height:1100px)]:text-xs [@media(min-height:1101px)]:text-base">
          {t('question_number', { question: currentIndex + 1, total: totalQuestions })} |{' '}
          {t('score', { count: score })}
        </h3>
      </div>
    </div>
  )
}

export default QuizPage
